<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useMediaQuery } from "@vueuse/core";
import { gsap } from "gsap";
import { useGlobalStore } from "@stores/global";
import { useGlobeStore } from "@stores/globe";
import { useDataStore } from "@stores/data";
import { useExpediaStore } from "@stores/expedia";

import IconX from "@components/icons/X.vue";
import IconCalendar from "@components/icons/Calendar.vue";
import IconPin from "@components/icons/Pin.vue";
import Button from "@components/Button.vue";
import IconArrowUpRight from "@components/icons/ArrowUpRight.vue";
import IconExpediaMark from "@components/icons/ExpediaMark.vue";
import IconEye from "@components/icons/Eye.vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";

const rootEl = ref(null);

function playReveal({ excludePersistent = false } = {}) {
    if (!rootEl.value) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const selector = excludePersistent
        ? "[data-reveal]:not([data-reveal-persistent])"
        : "[data-reveal]";
    const targets = rootEl.value.querySelectorAll(selector);
    if (!targets.length) return;

    gsap.killTweensOf(targets);
    gsap.fromTo(
        targets,
        { opacity: 0, y: 16 },
        {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: "power2.out",
            stagger: 0.06,
        },
    );
}

const globalStore = useGlobalStore();
const globeStore = useGlobeStore();
const dataStore = useDataStore();
const expediaStore = useExpediaStore();

const mode = ref("summary");
const activeTab = ref("info");
const isMobile = useMediaQuery("(max-width: 1024px)");

function close() {
    globeStore.clearSelection();
    globalStore.hidePanel("countryInfo");
    globalStore.hidePanel("clips");
    mode.value = "summary";
    activeTab.value = "info";
}

function openClip(clip) {
    globalStore.setModal([
        "clip",
        { clip, cityName: activeCountry.value },
        true,
        true,
        true,
    ]);
}

const activeCountry = computed(
    () => globeStore.selectedCountry ?? globeStore.hoveredCountry,
);

const activeContinent = computed(
    () => globeStore.selectedContinent ?? globeStore.hoveredContinent,
);

const isLocked = computed(() =>
    globeStore.selectedCountry
        ? globeStore.selectedLocked
        : globeStore.hoveredLocked,
);

const continentLabel = computed(
    () => dataStore.continentLabels[activeContinent.value] ?? "",
);

const activeCountryData = computed(() => {
    if (!activeCountry.value) return null;
    return dataStore.countryByName.get(activeCountry.value) ?? null;
});

const activeCity = computed(() => {
    if (globeStore.selectedCountry) {
        return dataStore.pinnedCities.find(
            (c) => c.name === globeStore.selectedCountry,
        );
    }
    if (globeStore.hoveredCountry) {
        return dataStore.pinnedCities.find(
            (c) => c.countryName === globeStore.hoveredCountry,
        );
    }
    return null;
});

const clips = computed(() => activeCity.value?.clips ?? []);

const activities = computed(() => activeCity.value?.activities ?? []);

const lastVisit = computed(() => {
    const date = activeCity.value?.lastVisit || activeCity.value?.unlockedAt;
    if (!date) return null;
    return new Date(date).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
    });
});

const placesVisited = computed(() => activeCity.value?.visited ?? 0);

const showDetails = computed(() => mode.value === "details");

watch(
    () => globeStore.selectedCountry,
    async (name) => {
        if (name) {
            mode.value = "details";
            activeTab.value = "info";
            await nextTick();
            playReveal();
        }
    },
    { immediate: true },
);

watch(activeTab, async () => {
    if (!isMobile.value || !showDetails.value) return;
    await nextTick();
    playReveal({ excludePersistent: true });
});

watch(
    () => globeStore.hoveredCountry,
    () => {
        if (!globeStore.selectedCountry) mode.value = "summary";
    },
);

function trackActivity(name) {
    fathom.trackEvent("Clicked Activity - " + name);
    if (typeof window.gtag === "function") {
        window.gtag("event", "clicked_activity", {
            activity_name: name,
            country_name: activeCity.value?.countryName ?? activeCountry.value,
            location_name: activeCity.value?.name,
        });
    }
}

function trackHotel() {
    fathom.trackEvent("Clicked Hotel - " + activeCity.value?.name);
    if (typeof window.gtag === "function") {
        window.gtag("event", "clicked_hotel", {
            country_name: activeCity.value?.countryName ?? activeCountry.value,
            location_name: activeCity.value?.name,
        });
    }
}

function trackFlight() {
    fathom.trackEvent("Clicked Flight - " + activeCity.value?.name);
    if (typeof window.gtag === "function") {
        window.gtag("event", "clicked_flight", {
            country_name: activeCity.value?.countryName ?? activeCountry.value,
            location_name: activeCity.value?.name,
        });
    }
}
</script>

<template>
    <Transition name="panel" appear>
        <div
            v-if="globalStore.panelActive('countryInfo')"
            ref="rootEl"
            class="country-info bg-marine-800 border-marine-500 flex flex-col border-b"
            :class="mode"
        >
            <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <div
                    class="relative flex shrink-0 flex-col gap-2 p-6 max-lg:pt-6"
                >
                    <div class="flex items-start justify-between">
                        <div class="flex flex-col gap-0.5 lg:gap-2">
                            <div class="flex items-center gap-2">
                                <p class="h8 font-bold uppercase">
                                    {{ continentLabel }}
                                </p>
                                <template
                                    v-if="
                                        showDetails && activeCity?.countryName
                                    "
                                >
                                    <svg
                                        class="size-4 text-yellow-400"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M6 4L10 8L6 12"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                    <p class="h8 font-bold uppercase">
                                        {{ activeCity.countryName }}
                                    </p>
                                </template>
                            </div>
                            <div
                                class="country-name lg:h3 h4 leading-none font-extrabold text-yellow-400 uppercase"
                            >
                                {{ activeCountry }}
                            </div>
                        </div>
                        <div class="size-8 shrink-0" v-if="showDetails">
                            <button
                                class="text-marine-800 border-marine-600 flex size-full items-center justify-center rounded-sm border-2 bg-yellow-400 shadow-[0_4px_0_0_var(--color-marine-600)]"
                                @click="close"
                                aria-label="Close"
                            >
                                <span class="sr-only">Close</span>
                                <IconX class="size-3.5" />
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    class="border-marine-400 hidden border-b max-lg:flex max-lg:px-4"
                    v-if="showDetails"
                >
                    <button
                        v-for="tab in [
                            { id: 'info', label: 'Info' },
                            ...(clips.length > 0
                                ? [
                                      {
                                          id: 'clips',
                                          label: `Clips (${clips.length})`,
                                      },
                                  ]
                                : []),
                            ...(activities.length > 0
                                ? [{ id: 'speed', label: 'Activities' }]
                                : []),
                        ]"
                        :key="tab.id"
                        :class="[
                            'body-sm flex h-10 flex-1 items-center justify-center border-b-2 px-2 py-4 font-medium uppercase transition-colors lg:px-6',
                            activeTab === tab.id
                                ? 'border-yellow-400 text-yellow-400'
                                : 'faded-text border-transparent',
                        ]"
                        @click="activeTab = tab.id"
                    >
                        {{ tab.label }}
                    </button>
                </div>

                <!-- Summary block: description + stats (visible on hover & details) -->
                <div
                    v-if="!showDetails || !isMobile || activeTab === 'info'"
                    class="flex shrink-0 flex-col gap-6 px-6 pb-4"
                >
                    <!-- Country description (hover/summary) -->
                    <p
                        v-if="!showDetails && activeCountryData?.description"
                        class="body-sm max-lg:pt-4"
                    >
                        {{ activeCountryData.description }}
                    </p>
                    <!-- City description (details) -->
                    <p
                        v-if="showDetails && activeCity?.description"
                        class="body-sm max-lg:pt-4"
                    >
                        {{ activeCity.description }}
                    </p>
                    <div
                        v-if="!showDetails && activeCity"
                        class="grid w-full grid-cols-2 gap-3"
                    >
                        <div v-if="placesVisited > 0">
                            <IconPin class="mb-3 size-4 text-yellow-400" />
                            <div class="flex flex-col gap-1.5">
                                <p
                                    class="body-sm leading-none font-bold uppercase"
                                >
                                    Places visited
                                </p>
                                <p
                                    class="body-md leading-none font-bold text-yellow-400 uppercase"
                                >
                                    {{ placesVisited }}
                                    {{
                                        placesVisited === 1 ? "place" : "places"
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="showDetails"
                    class="country-info__content px-6 pt-4 pb-0 lg:pb-6"
                >
                    <!-- Info tab (mobile) / full content (desktop) -->
                    <div
                        v-show="!isMobile || activeTab === 'info'"
                        class="flex flex-col gap-10"
                    >
                        <!-- Hotel booking card: shown on both desktop and mobile info tab -->
                        <div
                            v-if="showDetails && activeCity?.guide_url"
                            data-reveal
                            class="flex flex-col gap-3"
                        >
                            <p
                                class="body-sm faded-text leading-none font-bold uppercase"
                            >
                                Stay like Speed
                            </p>
                            <a
                                :href="expediaStore.href(activeCity.guide_url)"
                                target="_blank"
                                class="flex w-full items-center overflow-clip"
                                @click="trackHotel"
                            >
                                <img
                                    v-if="activeCity.guide_image"
                                    :src="activeCity.guide_image"
                                    :alt="activeCountry"
                                    class="bg-marine-400 size-16 shrink-0 object-cover"
                                />
                                <div
                                    class="-hotel-card bg-marine-600 flex grow flex-col justify-center gap-2 px-6 py-4"
                                >
                                    <p
                                        class="h8 leading-none font-extrabold text-yellow-400 uppercase"
                                    >
                                        Book hotels in
                                        {{ activeCountry }}
                                    </p>
                                    <p
                                        class="body-xs faded-text leading-none font-bold uppercase"
                                    >
                                        Explore stays on Expedia
                                    </p>
                                </div>
                            </a>
                        </div>

                        <!-- Desktop also shows the Speed sections below; mobile splits them into tabs -->
                        <template
                            v-if="
                                showDetails &&
                                !isMobile &&
                                activities.length > 0
                            "
                        >
                            <div data-reveal class="flex flex-col gap-3">
                                <p
                                    class="body-sm faded-text leading-none font-bold uppercase"
                                >
                                    Activities
                                </p>
                                <ul class="flex w-full flex-col gap-1">
                                    <li
                                        v-for="activity in activities"
                                        :key="activity.id"
                                        data-reveal
                                    >
                                        <a
                                            :href="
                                                expediaStore.href(
                                                    activity.guide_url,
                                                )
                                            "
                                            target="_blank"
                                            class="flex w-full items-center overflow-clip"
                                            @click="
                                                trackActivity(activity.name)
                                            "
                                        >
                                            <img
                                                :src="activity.guide_image"
                                                class="size-16 shrink-0 object-cover"
                                                :alt="activity.name"
                                            />
                                            <div
                                                class="flex grow flex-col gap-2 p-2"
                                            >
                                                <p
                                                    class="body-md leading-none font-bold uppercase"
                                                >
                                                    {{ activity.name }}
                                                </p>
                                            </div>
                                            <IconExpediaMark
                                                class="size-6 shrink-0 shadow-[0_10px_0_0_rgba(0,0,0,0.15)]"
                                            />
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </template>
                    </div>

                    <!-- Clips tab (mobile only) -->
                    <div
                        v-if="isMobile && activeTab === 'clips' && showDetails"
                        data-reveal
                        class="-mx-6"
                    >
                        <Swiper
                            :slides-per-view="1.2"
                            :space-between="12"
                            :slides-offset-before="24"
                            :slides-offset-after="24"
                        >
                            <SwiperSlide v-for="(clip, i) in clips" :key="i">
                                <button
                                    type="button"
                                    class="flex w-full cursor-pointer flex-col text-left"
                                    @click="openClip(clip)"
                                >
                                    <img
                                        :src="clip.poster"
                                        :alt="clip.title"
                                        class="bg-marine-400 aspect-video w-full object-cover"
                                    />
                                    <div
                                        class="bg-marine-600 flex flex-col gap-2 p-3"
                                    >
                                        <p
                                            class="body-md leading-none font-bold uppercase"
                                        >
                                            {{ clip.title }}
                                        </p>
                                        <div
                                            v-if="clip.views"
                                            class="flex items-center gap-1"
                                        >
                                            <IconEye class="size-3" />
                                            <p
                                                class="body-sm leading-none font-bold uppercase"
                                            >
                                                {{ clip.views }} Views
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            </SwiperSlide>
                        </Swiper>
                    </div>

                    <!-- What Speed did tab (mobile only) -->
                    <div
                        v-if="isMobile && activeTab === 'speed' && showDetails"
                        class="flex flex-col gap-10"
                    >
                        <div
                            v-if="activities.length > 0"
                            data-reveal
                            class="flex flex-col gap-3"
                        >
                            <p
                                class="body-sm faded-text leading-none font-bold uppercase"
                            >
                                Speed inspired activities
                            </p>
                            <ul class="flex w-full flex-col gap-1">
                                <li
                                    v-for="activity in activities"
                                    :key="activity.id"
                                    data-reveal
                                >
                                    <a
                                        :href="
                                            expediaStore.href(
                                                activity.guide_url,
                                            )
                                        "
                                        target="_blank"
                                        class="flex w-full items-center overflow-clip"
                                        @click="trackActivity(activity.name)"
                                    >
                                        <img
                                            :src="activity.guide_image"
                                            class="size-16 shrink-0 object-cover"
                                            :alt="activity.name"
                                        />
                                        <div
                                            class="flex grow flex-col gap-2 p-2"
                                        >
                                            <p
                                                class="body-md leading-none font-bold uppercase"
                                            >
                                                {{ activity.name }}
                                            </p>
                                        </div>
                                        <IconExpediaMark
                                            class="size-6 shrink-0 shadow-[0_10px_0_0_rgba(0,0,0,0.15)]"
                                        />
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="showDetails && activeCity?.flight_url"
                data-reveal
                data-reveal-persistent
                class="country-info__cta shrink-0 pt-4 max-lg:px-0 max-lg:pb-0 lg:p-6 lg:pt-4"
            >
                <Button
                    :href="expediaStore.href(activeCity?.flight_url)"
                    className="justify-between! px-4! w-full!"
                    target="_blank"
                    @click="trackFlight"
                >
                    <span>Book a flight on Expedia</span>
                    <div
                        class="bg-marine-600 flex size-8 items-center justify-center rounded-md text-yellow-400"
                    >
                        <IconArrowUpRight class="size-5" />
                    </div>
                </Button>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.country-info {
    grid-column: -7 / -1;
    z-index: 2;
}

.country-info.summary {
    grid-row: 2;
    align-self: start;
}

.country-info.details {
    grid-row: 2 / -1;
}

@media (max-width: 1024px) {
    .country-info,
    .country-info.summary,
    .country-info.details {
        grid-column: 1 / -1;
        grid-row: -2 / -1;
        align-self: end;
        max-height: 50svh;
        height: auto;
    }
}

.country-name {
    text-shadow: 0 0.15em 0 var(--marine-base, #19233b);
    -webkit-text-stroke-width: 0.125em;
    -webkit-text-stroke-color: var(--Marine-600, #19233b);
    paint-order: stroke fill;
}

.-hotel-card {
    background-image: repeating-linear-gradient(
        135deg,
        transparent 0,
        transparent 5px,
        var(--color-marine-700) 5px,
        var(--color-marine-700) 6px
    );
}
</style>
